import React from 'react';
import {
  Dialog,
  DialogPortal,
  DialogTitle,
  DialogDescription,
  InlineDialogOverlay,
  InlineDialogContent,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

interface RemoveProxyConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isMultiple: boolean;
  isResetOperation: boolean;
  container: HTMLElement;
}

export const RemoveProxyConfirmationDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isMultiple,
  isResetOperation,
  container,
}: RemoveProxyConfirmationDialogProps) => {
  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const getTitle = () => {
    if (isResetOperation) {
      return 'Reset returns proxy?';
    }
    return isMultiple ? 'Remove returns proxies?' : 'Remove returns proxy?';
  };

  const getDescription = () => {
    if (isResetOperation) {
      return 'Resetting this proxy will affect all portfolios and benchmarks that include this proxied investment in this view.';
    }
    return `Removing ${isMultiple ? 'these proxies ' : 'this proxy '} will affect all portfolios, benchmarks, and views that include ${isMultiple ? 'these proxied investments ' : 'the proxied investment '} across your workspace.`;
  };

  const getConfirmLabel = () => {
    if (isResetOperation) {
      return 'Reset Proxy';
    }
    return isMultiple ? 'Remove Proxies' : 'Remove Proxy';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPortal container={container}>
        <InlineDialogOverlay onClick={handleCancel} />
        <InlineDialogContent className="w-[550px] [&]:max-w-[550px] h-[280px] p-0 flex flex-col gap-0">
          <div className="h-[75px] items-center px-6 py-[16px] shrink-0">
            <DialogTitle className="text-[32px] font-normal leading-none">{getTitle()}</DialogTitle>
          </div>
          <Separator className="bg-venn-grey-30" />
          <div className="flex-1 px-[18px] py-[16px]">
            <DialogDescription className="text-[16px] text-venn-black">{getDescription()}</DialogDescription>
          </div>
          <Separator className="bg-venn-grey-30" />
          <div className="flex flex-row justify-between items-center gap-[6px] px-[18px] h-[64px] shrink-0">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="text-[11px] font-bold uppercase leading-none tracking-[1px]"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              className="uppercase text-[11px] font-bold tracking-[1px] leading-none bg-venn-error hover:bg-venn-strong-error"
            >
              {getConfirmLabel()}
            </Button>
          </div>
        </InlineDialogContent>
      </DialogPortal>
    </Dialog>
  );
};
